import React from 'react';
import { Crown, Lock, Dog, FileText, BrainCircuit, Bell } from 'lucide-react';

interface LimitReachedBannerProps {
  type?: 'pet' | 'log' | 'ai' | 'reminder';
  currentCount?: number;
  maxCount?: number;
  onUpgrade?: () => void;
  onDismiss?: () => void;
  show?: boolean;
  // Nouvelles props pour la version multi-limites
  reachedLimits?: Array<{
    type: 'pet' | 'log' | 'ai' | 'reminder';
    currentCount: number;
    maxCount: number;
  }>;
}

const LimitReachedBanner: React.FC<LimitReachedBannerProps> = ({
  type,
  currentCount,
  maxCount,
  onUpgrade,
  show = true,
  reachedLimits
}) => {
  if (!show) return null;

  // Si nous avons plusieurs limites atteintes, utiliser la vue multi-limites
  if (reachedLimits && reachedLimits.length > 0) {
    return (
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white p-4 rounded-2xl shadow-lg relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute -right-4 -bottom-4 text-white/10">
          <Lock size={64} />
        </div>
        
        <div className="relative z-10">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              <Crown size={20} className="text-yellow-300" />
              <h3 className="font-bold text-lg">
                {reachedLimits.length === 1 
                  ? "Limite atteinte" 
                  : `${reachedLimits.length} limites atteintes`
                }
              </h3>
            </div>
          </div>
          
          <p className="text-white/90 text-sm mb-3">
            {reachedLimits.length === 1 
              ? "Vous avez atteint une limite de votre abonnement gratuit."
              : "Vous avez atteint plusieurs limites de votre abonnement gratuit."
            }
          </p>
          
          {/* Liste des limites atteintes */}
          <div className="space-y-2 mb-3">
            {reachedLimits.map((limit, index) => {
              const getLimitInfo = () => {
                switch (limit.type) {
                  case 'pet':
                    return {
                      icon: <Dog size={16} />,
                      label: "Animaux",
                      text: `${limit.currentCount}/${limit.maxCount} animaux`
                    };
                  case 'log':
                    return {
                      icon: <FileText size={16} />,
                      label: "Logs",
                      text: `${limit.currentCount}/${limit.maxCount} suivis`
                    };
                  case 'ai':
                    return {
                      icon: <BrainCircuit size={16} />,
                      label: "Conversations IA",
                      text: `${limit.currentCount}/${limit.maxCount} conversations`
                    };
                  case 'reminder':
                    return {
                      icon: <Bell size={16} />,
                      label: "Rappels",
                      text: `${limit.currentCount}/${limit.maxCount} rappels`
                    };
                  default:
                    return {
                      icon: <Lock size={16} />,
                      label: "Limite",
                      text: `${limit.currentCount}/${limit.maxCount}`
                    };
                }
              };
              
              const info = getLimitInfo();
              
              return (
                <div key={index} className="flex items-center gap-2 bg-white/10 p-2 rounded-lg">
                  <span className="text-yellow-300">{info.icon}</span>
                  <div className="flex-1">
                    <span className="text-xs font-medium">{info.label}</span>
                    <span className="text-xs text-white/70 ml-2">{info.text}</span>
                  </div>
                </div>
              );
            })}
          </div>
          
          <button 
            onClick={onUpgrade}
            className="bg-white text-indigo-600 px-4 py-2 rounded-xl font-bold text-sm hover:bg-gray-50 transition-colors flex items-center gap-2"
          >
            <Crown size={16} />
            Passer à Premium - Débloquez tout
          </button>
        </div>
      </div>
    );
  }

  // Mode original pour une seule limite
  const getMessages = () => {
    switch (type) {
      case 'pet':
        return {
          title: "Limite d'animaux atteinte",
          description: `Vous avez ${currentCount} animal sur ${maxCount} autorisé(s) dans votre abonnement gratuit.`,
          action: "Ajoutez des animaux supplémentaires"
        };
      case 'log':
        return {
          title: "Limite de logs atteinte",
          description: `Vous avez ${currentCount} logs sur ${maxCount} autorisé(s) dans votre abonnement gratuit.`,
          action: "Suivez la santé de vos animaux sans limite"
        };
      case 'ai':
        return {
          title: "Limite de conversations IA atteinte",
          description: `Vous avez ${currentCount} conversations sur ${maxCount} autorisée(s) dans votre abonnement gratuit.`,
          action: "Obtenez des conseils illimités"
        };
      case 'reminder':
        return {
          title: "Limite de rappels atteinte",
          description: `Vous avez ${currentCount} rappel sur ${maxCount} autorisé(s) dans votre abonnement gratuit.`,
          action: "Programmez tous vos rappels vétérinaires"
        };
      default:
        return {
          title: "Limite atteinte",
          description: `Vous avez atteint la limite de ${maxCount}.`,
          action: "Passez à la version premium"
        };
    }
  };

  const messages = getMessages();

  return (
    <div className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white p-4 rounded-2xl shadow-lg relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute -right-4 -bottom-4 text-white/10">
        <Lock size={64} />
      </div>
      
      <div className="relative z-10">
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2">
            <Crown size={20} className="text-yellow-300" />
            <h3 className="font-bold text-lg">{messages.title}</h3>
          </div>
        </div>
        
        <p className="text-white/90 text-sm mb-3">{messages.description}</p>
        
        <button 
          onClick={onUpgrade}
          className="bg-white text-indigo-600 px-4 py-2 rounded-xl font-bold text-sm hover:bg-gray-50 transition-colors flex items-center gap-2"
        >
          <Crown size={16} />
          Passer à Premium - {messages.action}
        </button>
      </div>
    </div>
  );
};

export default LimitReachedBanner;
